/* eslint-disable no-console */
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React from 'react';
import { BookingDetailsAdditionalService, addEditService, editEditService, removeEditService } from '../../../../../store/slices/bookingCreationSlice';
import { AdditionalService } from '../../../../../types/bookingCreation';
import { useTranslation } from 'react-i18next';
import ServiceItem from '../../../CreateBooking/components/AdditionalServices/ServiceItem';
import UnavailableServicesNotice from './UnavailableServicesNotice';
import { useDispatch } from 'react-redux';

export interface AdditionalServicesCallbacks {
    onAddService: (args: { serviceId: string, name: string, quantity: number, unitPrice: number }) => void;
    onEditService: (args: { serviceId: string, name: string, quantity: number }) => void;
    onRemoveService: (name: string) => void;
}

interface Props {
    additionalServices: BookingDetailsAdditionalService[];
    allAdditionalServices: AdditionalService[];
    // When provided, these callbacks replace the default Redux dispatches.
    // Used by flows that manage selection state locally (e.g. series-level edit).
    callbacks?: AdditionalServicesCallbacks;
    hint?: string;
    disabled?: boolean;
    // Booked add-ons that don't exist at the asset the admin switched to
    // during edit — rendered unchecked below the lists with a notice.
    unavailableServices?: BookingDetailsAdditionalService[];
    // Creation timestamp of the booking being edited (#1734). When set, catalog
    // services added to the asset *after* the booking was made are hidden: they
    // were never on offer for this booking. Omit it in flows that price fresh
    // (create, or a series-level edit that re-prices every occurrence).
    bookingCreatedAt?: string | null;
    // The booking's compulsory lines as booked (`payment.compulsory`). They are
    // not part of `additionalServices` — the API delivers only optional lines
    // there — so without this a compulsory service would still be shown at
    // today's catalog price while the backend bills the snapshot (#1734).
    bookedCompulsoryServices?: Array<{ id: string, unitPrice: number }>;
}

const BookingEditDetailsAdditionalServices = ({
    additionalServices,
    allAdditionalServices,
    callbacks,
    hint,
    disabled,
    unavailableServices,
    bookingCreatedAt,
    bookedCompulsoryServices,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // #1734: what this booking may be priced against. A service already on the
    // booking shows the price it was booked at, not the catalog's current one;
    // a service the catalog gained after the booking was made is not offered at
    // all. Both fall back to catalog behaviour when the data can't prove
    // otherwise (no snapshot price, or an unknown `createdAt`).
    const availableServices = React.useMemo(() => {
        const bookedPriceById = new Map<string, number>();
        for (const service of additionalServices) {
            if (service.id && service.unitPrice !== undefined && service.unitPrice !== null) {
                bookedPriceById.set(service.id, service.unitPrice);
            }
        }
        for (const service of bookedCompulsoryServices ?? []) {
            if (service.id && service.unitPrice !== undefined && service.unitPrice !== null) {
                bookedPriceById.set(service.id, service.unitPrice);
            }
        }
        const bookedAt = bookingCreatedAt ? new Date(bookingCreatedAt).getTime() : null;

        return allAdditionalServices.filter((service) => {
            if (bookedPriceById.has(service.id)) return true;
            if (bookedAt === null || !service.createdAt) return true;
            const addedAt = new Date(service.createdAt).getTime();
            return Number.isNaN(addedAt) || addedAt <= bookedAt;
        }).map((service) => {
            const bookedPrice = bookedPriceById.get(service.id);
            return bookedPrice === undefined ? service : { ...service, unitPrice: bookedPrice };
        });
    }, [allAdditionalServices, additionalServices, bookedCompulsoryServices, bookingCreatedAt]);

    const [quantities, setQuantities] = React.useState<Record<string, number>>({});

    const [checkedServices, setCheckedServices] = React.useState<Record<string, boolean>>(
        availableServices.reduce<Record<string, boolean>>((acc, service) => {
            acc[service.serviceName] = service.isPaymentCompulsory === true;
            return acc;
        }, {}),
    );

    const emitAdd = (args: { serviceId: string, name: string, quantity: number, unitPrice: number }) => {
        if (callbacks) callbacks.onAddService(args);
        else dispatch(addEditService(args));
    };
    const emitEdit = (args: { serviceId: string, name: string, quantity: number }) => {
        if (callbacks) callbacks.onEditService(args);
        else dispatch(editEditService(args));
    };
    const emitRemove = (name: string) => {
        if (callbacks) callbacks.onRemoveService(name);
        else dispatch(removeEditService({ name }));
    };

    const handleCheckboxChange = (serviceName: string) => {
        if (disabled) return;
        const targetService = availableServices.find((service) => service.serviceName === serviceName);
        if (targetService?.isPaymentCompulsory) {
            return;
        }
        setCheckedServices((prevState) => {
            const newCheckedState = {
                ...prevState,
                [serviceName]: !prevState[serviceName],
            };

            if (!prevState[serviceName]) {
                emitAdd({
                    serviceId: targetService?.id!,
                    name: serviceName,
                    quantity: quantities[serviceName],
                    // Booked price when this line is already on the booking,
                    // current catalog price when it is genuinely new (#1734).
                    unitPrice: targetService?.unitPrice!,
                });
            } else {
                emitRemove(serviceName);
            }

            return newCheckedState;
        });
    };

    const handleDecrement = (serviceName: string) => {
        if (disabled) return;
        setQuantities((prevQuantities) => {
            const newQuantities = {
                ...prevQuantities,
                [serviceName]: Math.max(1, prevQuantities[serviceName] - 1),
            };
            emitEdit({
                serviceId: availableServices.find((service) => service.serviceName === serviceName)?.id!,
                name: serviceName,
                quantity: newQuantities[serviceName],
            });
            return newQuantities;
        });
    };

    const handleIncrement = (serviceName: string) => {
        if (disabled) return;
        setQuantities((prevQuantities) => {
            const newQuantities = {
                ...prevQuantities,
                [serviceName]: prevQuantities[serviceName] + 1,
            };
            emitEdit({
                serviceId: availableServices.find((service) => service.serviceName === serviceName)?.id!,
                name: serviceName,
                quantity: newQuantities[serviceName],
            });
            return newQuantities;
        });
    };

    React.useEffect(() => {
        if (availableServices.length > 0) {
            const initialQuantities = availableServices.reduce<Record<string, number>>((acc, service) => {
                acc[service.serviceName] = 1;
                return acc;
            }, {});
            setQuantities(initialQuantities);
        }
    }, [availableServices]);

    React.useEffect(() => {
        // Re-derive even when the selection is empty — after a venue/apartment
        // switch the catalog changes and stale checkmarks from the previous
        // asset must not survive (same service names, different service rows).
        const updatedQuantities = additionalServices.reduce<Record<string, number>>((acc, item) => {
            const matchedService = availableServices.find((s) => s.id === item.id);
            if (matchedService && item.quantity) {
                acc[matchedService.serviceName] = item.quantity;
            }
            return acc;
        }, {});
        setQuantities((prev) => ({ ...prev, ...updatedQuantities }));

        // Set checkboxes for selected services (mandatory services are always checked)
        const updatedChecked = availableServices.reduce<Record<string, boolean>>((acc, service) => {
            acc[service.serviceName] =
                service.isPaymentCompulsory === true ||
                additionalServices.some((item) => item.id === service.id);
            return acc;
        }, {});
        setCheckedServices(updatedChecked);
    }, [additionalServices, availableServices]);

    const visibleServices = availableServices.filter((item) => item?.langNames?.en !== 'Internet');
    // `availableServices` carries the booked price for lines already on the
    // booking, so the catalog's own price has to be looked up separately to
    // name it beside them when the two differ (#1735).
    const catalogPriceById = React.useMemo(
        () => new Map(allAdditionalServices.map((service) => [service.id, service.unitPrice])),
        [allAdditionalServices],
    );
    // #1734: while editing on the original asset, the mandatory section shows
    // the booking's OWN compulsory set (`payment.compulsory`, era-aware on the
    // backend) — not the catalog's. A service made compulsory after the
    // booking is not billed to it, so displaying it checked here would
    // contradict the total below. On an asset switch (`bookingCreatedAt`
    // unset) the new asset's catalog applies unchanged.
    const bookedCompulsoryIds =
        bookingCreatedAt && bookedCompulsoryServices
            ? new Set(bookedCompulsoryServices.map((s) => s.id))
            : null;
    const hasAnyVisibleService = visibleServices.length > 0;
    const mandatoryServices = visibleServices.filter(
        (s) =>
            s.isPaymentCompulsory === true &&
            (bookedCompulsoryIds === null || bookedCompulsoryIds.has(s.id)),
    );
    const optionalServices = visibleServices.filter((s) => !s.isPaymentCompulsory);

    return (
        <Box>
            {!hasAnyVisibleService && (
                <Typography color={'text.secondary'} className={'text-sm'}>
                    {t('create_booking.additional_services.empty_state')}
                </Typography>
            )}
            {hasAnyVisibleService && (
                <>
                    <Typography variant={'subtitle1'} className={'font-medium mt-2 mb-1'}>
                        {t('create_booking.additional_services.mandatory_title')}
                    </Typography>
                    {mandatoryServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm mb-2'}>
                            {t('create_booking.additional_services.empty_mandatory')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1} className={'mb-2'}>
                            {mandatoryServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    <ServiceItem
                                        service={service}
                                        isChecked={checkedServices[service.serviceName]}
                                        quantity={quantities[service.serviceName]}
                                        onToggle={() => handleCheckboxChange(service.serviceName)}
                                        onIncrement={() => handleIncrement(service.serviceName)}
                                        onDecrement={() => handleDecrement(service.serviceName)}
                                        isMandatory={service.isPaymentCompulsory}
                                        currentPrice={catalogPriceById.get(service.id)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    <Typography variant={'subtitle1'} className={'font-medium mt-3 mb-1'}>
                        {t('create_booking.additional_services.optional_title')}
                    </Typography>
                    {optionalServices.length === 0 ? (
                        <Typography color={'text.secondary'} className={'text-sm'}>
                            {t('create_booking.additional_services.empty_optional')}
                        </Typography>
                    ) : (
                        <Grid container spacing={1}>
                            {optionalServices.map((service) => (
                                <Grid item xs={12} sm={6} key={service.id}>
                                    <ServiceItem
                                        service={service}
                                        isChecked={checkedServices[service.serviceName]}
                                        quantity={quantities[service.serviceName]}
                                        onToggle={() => handleCheckboxChange(service.serviceName)}
                                        onIncrement={() => handleIncrement(service.serviceName)}
                                        onDecrement={() => handleDecrement(service.serviceName)}
                                        isMandatory={service.isPaymentCompulsory}
                                        currentPrice={catalogPriceById.get(service.id)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}
            {hint && (
                <Typography variant={'caption'} sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                    {hint}
                </Typography>
            )}
            <UnavailableServicesNotice services={unavailableServices ?? []} />
        </Box>
    );
};

export default BookingEditDetailsAdditionalServices;
